/**
 * SimilarPlayersTabView - Responsive layout for Similar Players tab
 *
 * Desktop: two-column layout with filter panel on the left and player list on the right.
 * Mobile: single-column with a "Filters" button that opens a full-screen overlay,
 * and player rows rendered as individual bordered cards.
 * Includes watchlist action sheet for adding players to lists.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { SlidersHorizontal } from "lucide-react-native";
import React, { memo, RefObject } from "react";

import SimilarPlayersFilterPanel from "@/features/player-detail/components/organisms/SimilarPlayersFilterPanel";

import AddToWatchlistActionsheet from "@/components/organisms/AddToWatchlistActionsheet";
import FullScreenOverlay from "@/components/organisms/shared/FullScreenOverlay";
import CustomPromptContainer, {
  CustomPromptRefProps,
} from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

import {
  PlayerSearchPlayerFragment,
  SimilarPlayerSortField,
  SimilarPlayersPlayerResultFragment,
} from "@/generated/graphql";
import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

import {
  SimilarPlayersFilterData,
  SimilarPlayersFilterCallbacks,
  SimilarPlayersListState,
} from "@/features/player-detail/types";

/**
 * Player-list height cap (consumed by a gluestack ui Box).
 * design-system: dynamic-style waiver — fixed 600px list cap, off the sizing ladder
 */
const LIST_MAX_HEIGHT_STYLE = { maxHeight: 600 };

/**
 * Props for SimilarPlayersTabView component
 */
interface SimilarPlayersTabViewProps
  extends SimilarPlayersFilterData, SimilarPlayersFilterCallbacks {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Whether the viewport is a mobile screen */
  readonly isMobile: boolean;
  /**
   * Resolution state of the similar-players read (SE-7580). Replaces the old
   * `loading` + `similarPlayers` pair, which could not tell a failed read
   * apart from a genuinely empty one.
   */
  readonly listState: SimilarPlayersListState;
  /** Whether data is refreshing */
  readonly refreshing: boolean;
  /** Committed sort field (drives badge display in player list) */
  readonly committedSortField: SimilarPlayerSortField;
  /** Whether any filter has pending uncommitted changes */
  readonly hasPendingChanges: boolean;
  /** Whether filters are at their default values */
  readonly isDefaultFilters: boolean;
  /** Currently selected player for watchlist action */
  readonly selectedPlayer: PlayerSearchPlayerFragment | undefined;
  /** Whether watchlist action sheet is visible */
  readonly showActionSheet: boolean;
  /** Whether the filter modal is visible (mobile only) */
  readonly showFilterModal: boolean;
  /** Ref for custom prompt component */
  /** The list body — spinner, error, empty or rows — assembled by the Container. */
  readonly listContent: React.ReactNode;
  /** The filter panel's data and callbacks, assembled by the Container. */
  readonly filterPanelProps: React.ComponentProps<
    typeof SimilarPlayersFilterPanel
  >;
  readonly promptRef: RefObject<CustomPromptRefProps>;
  /** Callback when Save button is pressed */
  readonly onSaveFilters: () => void;
  /** Callback when Reset button is pressed */
  readonly onResetFilters: () => void;
  /** Callback to refresh data */
  readonly onRefresh: () => void;
  /** Callback to load more data */
  readonly onLoadMore: () => void;
  /** Callback when add button is pressed on a player row */
  readonly onAddPress: (player: SimilarPlayersPlayerResultFragment) => void;
  /** Callback when a player row is pressed */
  readonly onRowPress: (playerId: string) => string;
  /** Callback to close the action sheet */
  readonly onCloseActionSheet: () => void;
  /** Callback to add to watchlist */
  readonly onAddWatchlistPress: () => void;
  /** Callback to open the filter modal (mobile only) */
  readonly onOpenFilterModal: () => void;
  /** Callback to close the filter modal (mobile only) */
  readonly onCloseFilterModal: () => void;
}

/**
 * Renders the Similar Players tab with responsive layout
 * @param props - Component props (see SimilarPlayersTabViewProps interface)
 * @returns Desktop: two-column layout; Mobile: single-column with filter modal
 */
const SimilarPlayersTabView = React.memo<SimilarPlayersTabViewProps>(
  ({
    isDark,
    isMobile,
    selectedPlayer,
    showActionSheet,
    showFilterModal,
    promptRef,
    onCloseActionSheet,
    onAddWatchlistPress,
    onOpenFilterModal,
    onCloseFilterModal,
    listContent,
    filterPanelProps,
  }) => (
    <>
      {isMobile ? (
        <Stack className="flex-1 pt-2">
          {/* Filters Button */}
          <Box className="px-4 pb-2">
            <Pressable
              onPress={onOpenFilterModal}
              className="items-center justify-center rounded-md border border-outline-subtle bg-surface-base p-4 active:bg-surface-raised"
            >
              <Stack direction="horizontal" className="items-center gap-2">
                <Icon
                  as={SlidersHorizontal}
                  size="sm"
                  className="text-content-muted"
                />
                <Text
                  variant="title-sm-medium"
                  className={`${
                    isDark
                      ? "text-surface-strong"
                      : "text-content-neutral-strong"
                  }`}
                >
                  Filters
                </Text>
              </Stack>
            </Pressable>
          </Box>

          {/* Player List - full width */}
          <Box
            {...{ "data-testid": TEST_ID_PLAYER_DETAIL.SIMILAR_PLAYERS_LIST }}
            className="flex-1"
          >
            {listContent}
          </Box>

          {/* Filter Modal */}
          <FullScreenOverlay
            isOpen={showFilterModal}
            onClose={onCloseFilterModal}
            title="Filters"
          >
            <SimilarPlayersFilterPanel {...filterPanelProps} isFullWidth />
          </FullScreenOverlay>
        </Stack>
      ) : (
        <Stack
          direction="horizontal"
          className="size-full min-h-0 flex-1 gap-6 px-4 pt-4"
        >
          {/* Left Column - Filter Panel */}
          <Box className="shrink-0">
            <SimilarPlayersFilterPanel {...filterPanelProps} />
          </Box>

          {/* Right Column - Player List */}
          <Box
            {...{ "data-testid": TEST_ID_PLAYER_DETAIL.SIMILAR_PLAYERS_LIST }}
            className={`flex-1 rounded-md border ${
              isDark
                ? "border-border-outline-subtle bg-surface-base"
                : "border-border-outline-subtle bg-surface-raised"
            }`}
            // eslint-disable-next-line no-restricted-syntax -- fixed 600px desktop list-column cap is off the sizing ladder (max-h token 96=384px); arbitrary values banned, so runtime UNSAFE_style is the sanctioned mechanism
            UNSAFE_style={LIST_MAX_HEIGHT_STYLE}
          >
            {listContent}
          </Box>
        </Stack>
      )}

      {/* Watchlist Action Sheet */}
      {selectedPlayer && (
        <AddToWatchlistActionsheet
          item={selectedPlayer}
          showActionsheet={showActionSheet}
          onClose={onCloseActionSheet}
          onAddPress={onCloseActionSheet}
          onAddWatchlistPress={onAddWatchlistPress}
          loading={false}
          isPlayerInAnyWatchlist={
            selectedPlayer.isWatchedByCurrentUser ?? false
          }
          promptRef={promptRef}
          isDark={isDark}
        />
      )}

      <CustomPromptContainer ref={promptRef} />
    </>
  )
);

SimilarPlayersTabView.displayName = "SimilarPlayersTabView";

export default memo(SimilarPlayersTabView);
